import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MoveHorizontal } from 'lucide-react';
import { getLatestS3Image, getFirstS3Image } from '../api/PublicIframe.api';
import '../styles/PublicIframe.css';
import '../styles/PublicCompareIframe.css';

const preloadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = reject;
    img.src = url;
  });

const PublicCompareIframe = ({ feedId, label = 'Antes / Después', refreshInterval = 60000 }) => {
  const [firstImageUrl, setFirstImageUrl] = useState(null);
  const [lastImageUrl, setLastImageUrl] = useState(null);
  const [lastImageDate, setLastImageDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [sliderPos, setSliderPos] = useState(50);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const fetchFirstImage = useCallback(async () => {
    try {
      const data = await getFirstS3Image(feedId);
      await preloadImage(data.url);
      setFirstImageUrl(data.url);
    } catch (err) {
      console.error('Error fetching first image:', err);
      setErrorMessage('Todavía no hay imagen inicial configurada');
      setError(true);
    }
  }, [feedId]);

  const fetchLastImage = useCallback(async () => {
    try {
      const data = await getLatestS3Image(feedId);
      await preloadImage(data.url);
      setLastImageUrl(data.url);
      setLastImageDate(data.date);
    } catch (err) {
      console.error('Error fetching last image:', err);
      setErrorMessage('No se pudo cargar la última imagen');
      setError(true);
    }
  }, [feedId]);

  useEffect(() => {
    if (!feedId) {
      setError(true);
      setErrorMessage('Falta el parámetro feedId');
      return;
    }

    let cancelled = false;

    (async () => {
      setError(false);
      await Promise.all([fetchFirstImage(), fetchLastImage()]);
      if (!cancelled) setLoading(false);
    })();

    const interval = setInterval(fetchLastImage, refreshInterval);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [feedId, refreshInterval, fetchFirstImage, fetchLastImage]);

  const updateSliderFromClientX = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pos = ((clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.min(100, Math.max(0, pos)));
  };

  const handleMouseMove = useCallback((e) => {
    if (!draggingRef.current) return;
    updateSliderFromClientX(e.clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!draggingRef.current) return;
    if (e.touches && e.touches[0]) {
      updateSliderFromClientX(e.touches[0].clientX);
    }
  }, []);

  const stopDragging = useCallback(() => {
    draggingRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', stopDragging);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [handleMouseMove, handleTouchMove, stopDragging]);

  const handleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const ready = !loading && !error && firstImageUrl && lastImageUrl;

  return (
    <div className="lsl lsc" id="lsc" ref={containerRef}>
      {ready && (
        <>
          <img className="lsc-img lsc-img-after" alt="Última imagen" src={lastImageUrl} />
          <div
            className="lsc-before-wrap"
            style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
          >
            <img className="lsc-img lsc-img-before" alt="Primera imagen" src={firstImageUrl} />
          </div>

          <div
            className="lsc-divider"
            style={{ left: `${sliderPos}%` }}
            onMouseDown={() => { draggingRef.current = true; }}
            onTouchStart={() => { draggingRef.current = true; }}
          >
            <div className="lsc-handle">
              <MoveHorizontal size={18} />
            </div>
          </div>
        </>
      )}

      <div className="lsl-logo">
        <span className="lsl-mark"></span>
        {feedId !== 'liwkb09qt3j6' && <span>LISUAL</span>}
      </div>

      <div className="lsl-ctrl">
        <button
          className="lsl-btn"
          title="Pantalla completa"
          onClick={handleFullscreen}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4"></path>
          </svg>
        </button>
      </div>

      <div className="lsl-meta">
        <span className="lsl-label">
          {label}
        </span>
        <span className="lsl-stat">
          <span className="lsl-time">
            {formatTime(lastImageDate)}
          </span>
          <span className="lsl-badge live">
            <span className="lsl-dot"></span>
            <span>LIVE</span>
          </span>
        </span>
      </div>

      {loading && !error && (
        <div className="lsl-cover">
          <div className="lsl-spin"></div>
          <small>Cargando</small>
        </div>
      )}

      {error && (
        <div className="lsl-cover">
          <span className="ico">⚠</span>
          <small>{errorMessage || 'Sin señal'}</small>
        </div>
      )}
    </div>
  );
};

export default PublicCompareIframe;
